import { Col, Flex, Input, Select } from 'antd';
import { useCallback, type VFC } from 'react';
import { FilterControlButtons } from 'components/form/FilterControlButtons';
import { Button, Filter, FilterDivider, Form } from 'components';
import { Icon } from 'components/icon/Icon';
import { OutputTechnology, RoleName } from '@enmon/commons/lib/tools';
import { outputTechnologyTranslated } from 'routes/portfolio/locations/const';
import { MeterModelsSelect } from 'components/form/select/MeterModelsSelect';
import type { Paths } from 'type-fest';
import { useHistory } from 'react-router-dom';
import { useUserInfo } from '@/hooks/useUser';
import {
  type MeterModulesFilter,
  DEFAULT_FILTER,
  useModulesListFilterWithPagination,
} from '../hooks/useModulesListFilterWithPagination';

export const ModulesListFilter: VFC = () => {
  const [form] = Form.useForm<MeterModulesFilter>();
  const history = useHistory();
  const { role } = useUserInfo() ?? {};
  const isMagicRole = role?.name === RoleName.Magic;

  const { filter, setFilterState, resetFilterState, setPaginationState } = useModulesListFilterWithPagination();

  const handleReset = () => {
    form.setFieldsValue(DEFAULT_FILTER);

    resetFilterState();
    setPaginationState({
      current: 1,
    });
  };

  const handleCreate = useCallback(() => {
    history.push(`${history.location.pathname}/create`);
  }, [history]);

  const handleFinish = (values: MeterModulesFilter) => {
    setFilterState(values);
    setPaginationState({
      current: 1,
    });
  };

  return (
    <Filter form={form} initialValues={filter} onFinish={handleFinish}>
      <Form.Item label="Search" name={'search' satisfies Paths<MeterModulesFilter>}>
        <Input allowClear placeholder="Module name..." />
      </Form.Item>

      <Form.Item label="Meter models" name={'meterModels' satisfies Paths<MeterModulesFilter>}>
        <MeterModelsSelect
          allowClear
          maxTagCount="responsive"
          maxTagTextLength={20}
          mode="multiple"
          placeholder="Select meter models"
        />
      </Form.Item>

      <Form.Item label="Technologies" name={'technologies' satisfies Paths<MeterModulesFilter>}>
        <Select
          allowClear
          maxTagTextLength={20}
          mode="multiple"
          options={Object.values(OutputTechnology).map(value => ({
            label: outputTechnologyTranslated[value],
            value,
          }))}
          placeholder="Select technologies"
        />
      </Form.Item>

      <FilterDivider />

      <Flex align="end" flex={2} justify="space-between">
        <Col>
          <FilterControlButtons onClickReset={handleReset} />
        </Col>

        {isMagicRole && (
          <Col>
            <Button icon={<Icon iconString="plus" />} onClick={handleCreate}>
              Create new module
            </Button>
          </Col>
        )}
      </Flex>
    </Filter>
  );
};
